import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fingerprint, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useState, type FormEvent, type JSX } from "react";
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  PasswordInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@mailflare/ui";
import type { TotpSetupResponse } from "@mailflare/shared";

import { queryKeys } from "@/lib/query-keys";
import { deletePasskey, totpDisable, totpEnable, totpSetup } from "@/features/auth/api";
import { usePasskeys, useSession } from "@/features/auth/hooks";
import { registerPasskey } from "@/features/auth/passkey";

import { errorText } from "@/features/auth/components/auth-card";

export function SecuritySection(): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const session = useSession();
  const passkeys = usePasskeys();
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");

  const addPasskey = useMutation({
    mutationFn: (name: string) => registerPasskey(name),
    onSuccess: () => {
      toast({ title: "Passkey added", variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.passkeys });
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
    onError: (error) => {
      toast({
        title: "Could not add passkey",
        description: errorText(error),
        variant: "destructive",
      });
    },
  });

  const removePasskey = useMutation({
    mutationFn: (id: string) => deletePasskey(id),
    onSuccess: () => {
      toast({ title: "Passkey removed", variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.passkeys });
    },
    onError: (error) => {
      toast({
        title: "Could not remove passkey",
        description: errorText(error),
        variant: "destructive",
      });
    },
  });

  const beginTotp = useMutation({
    mutationFn: totpSetup,
    onSuccess: setSetup,
    onError: (error) => {
      toast({
        title: "Could not start TOTP setup",
        description: errorText(error),
        variant: "destructive",
      });
    },
  });

  const confirmTotp = useMutation({
    mutationFn: () => totpEnable({ code }),
    onSuccess: () => {
      toast({ title: "Two-factor enabled", variant: "success" });
      setSetup(null);
      setCode("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
    onError: (error) => {
      toast({ title: "Invalid code", description: errorText(error), variant: "destructive" });
    },
  });

  const disableTotp = useMutation({
    mutationFn: () => totpDisable({ code, password: disablePassword }),
    onSuccess: () => {
      toast({ title: "Two-factor disabled", variant: "success" });
      setDisablePassword("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
    onError: (error) => {
      toast({
        title: "Could not disable two-factor",
        description: errorText(error),
        variant: "destructive",
      });
    },
  });

  const handleAddPasskey = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("passkey-name");
    addPasskey.mutate(typeof name === "string" && name.length > 0 ? name : "Passkey");
  };

  const totpEnabled = session.data?.user.totpEnabled ?? false;
  const userPasskeys = passkeys.data ?? [];

  return (
    <div className="space-y-8">
      <section aria-labelledby="passkeys-heading">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint aria-hidden className="size-4" />
              <h2 id="passkeys-heading">Passkeys</h2>
            </CardTitle>
            <CardDescription>Sign in without a password using WebAuthn.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {passkeys.isLoading ? (
              <div role="status" aria-label="Loading passkeys" className="space-y-2">
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-11 w-full" />
              </div>
            ) : userPasskeys.length === 0 ? (
              <EmptyState
                icon={Fingerprint}
                title="No passkeys registered."
                description="Add a passkey to sign in without a password."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userPasskeys.map((passkey) => (
                    <TableRow key={passkey.id}>
                      <TableCell className="font-medium">{passkey.name}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        Added {passkey.createdAt}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove passkey ${passkey.name}`}
                          onClick={() => removePasskey.mutate(passkey.id)}
                        >
                          <Trash2 aria-hidden className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <form className="flex items-end gap-2" onSubmit={handleAddPasskey}>
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="passkey-name">New passkey name</Label>
                <Input id="passkey-name" name="passkey-name" placeholder="Laptop" />
              </div>
              <Button type="submit" disabled={addPasskey.isPending}>
                <Plus aria-hidden className="size-4" />
                Add
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="totp-heading">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck aria-hidden className="size-4" />
              <h2 id="totp-heading">Two-factor authentication</h2>
            </CardTitle>
            <CardAction>
              <Badge variant={totpEnabled ? "secondary" : "outline"}>
                {totpEnabled ? "Enabled" : "Off"}
              </Badge>
            </CardAction>
          </CardHeader>
          <CardContent className="space-y-4">
            {!totpEnabled && setup === null ? (
              <Button
                variant="outline"
                onClick={() => beginTotp.mutate()}
                disabled={beginTotp.isPending}
              >
                Set up authenticator app
              </Button>
            ) : null}

            {setup !== null ? (
              <div className="bg-muted/40 space-y-3 rounded-lg border p-4">
                <p className="text-muted-foreground text-sm">
                  Add this secret to your authenticator app, then enter the 6-digit code.
                </p>
                <code className="bg-muted block rounded p-2 font-mono text-xs break-all">
                  {setup.secret}
                </code>
                <a className="text-primary text-xs underline" href={setup.otpauthUrl}>
                  Open in authenticator app
                </a>
                <div className="flex items-end gap-2">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="totp-confirm">Code</Label>
                    <Input
                      id="totp-confirm"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))}
                    />
                  </div>
                  <Button
                    onClick={() => confirmTotp.mutate()}
                    disabled={code.length !== 6 || confirmTotp.isPending}
                  >
                    Enable
                  </Button>
                </div>
              </div>
            ) : null}

            {totpEnabled ? (
              <div className="space-y-3 rounded-lg border p-4">
                <p className="text-sm font-medium">Disable two-factor</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="totp-disable-code">Code</Label>
                    <Input
                      id="totp-disable-code"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="totp-disable-password">Password</Label>
                    <PasswordInput
                      id="totp-disable-password"
                      value={disablePassword}
                      onChange={(event) => setDisablePassword(event.target.value)}
                    />
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => disableTotp.mutate()}
                  disabled={
                    code.length !== 6 || disablePassword.length === 0 || disableTotp.isPending
                  }
                >
                  Disable
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
